import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useProgress } from '../context/ProgressContext'
import { useConfig } from '../context/ConfigContext'
import { courseHref, findCourse } from '../data/courses'
import { isCourseVisible, mergeCatalogue, publishedInCourse } from '../data/catalogue'
import { useAuth } from '../context/AuthContext'
import { modules as builtInModules, getCourse } from '../data/modules'
import { BookOpen, ChevronRight, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react'

export default function Home() {
  const { user } = useAuth()
  const { progressForCourse, myCourseIds, otherCourseIds, myDegrees, progressForDegree } = useProgress()
  const { customModules, allCourses } = useConfig()

  /** How many visible courses a degree is delivered through. */
  const coursesIn = (degree) =>
    (degree.tracks || []).filter((id) =>
      isCourseVisible(findCourse(allCourses, id), customModules)).length

  // Yours = assigned to you, or started by you. The rest are listed too and
  // anyone may do them — enrolment says what you are expected to do, not what
  // you are allowed to do, and the content is in the same bundle either way.
  const mine = myCourseIds.map((id) => getCourse(id, allCourses)).filter(Boolean)
  const others = otherCourseIds.map((id) => getCourse(id, allCourses)).filter(Boolean)
  const anyStarted = mine.some((t) => progressForCourse(t.id).started)

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-6xl mb-6">🎓</div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              Finder University
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Short, practical courses you work through at your own pace.
              Learn by doing it on a real spreadsheet — not by watching someone else.
            </p>

            <div className="flex items-center justify-center gap-4">
              {/* Scoped to the trainee's own first course. A bare /modules
                  lists every course and scrolls to the first incomplete module
                  across all of them, so a Finance starter clicking this would
                  be dropped into the seventeen-module Sheets course — the same
                  bug the course cards had in August, which a third course makes
                  more likely rather than less. */}
              <Link
                to={mine[0] ? courseHref(mine[0].id) : '/'}
                className="inline-flex items-center gap-2 bg-white text-blue-700 font-semibold px-8 py-3 rounded-xl hover:bg-blue-50 transition-colors text-lg"
              >
                <BookOpen size={20} />
                {anyStarted ? 'Continue Learning' : 'Start Learning'}
                <ChevronRight size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Degrees — the thing someone is actually enrolled in. Courses are
          how it is delivered; this is what it adds up to. */}
      {myDegrees.length > 0 && (
        <div className="mx-auto w-full max-w-4xl px-6 pt-12">
          <h2 className="mb-5 text-sm font-semibold uppercase tracking-wide text-slate-400">
            {myDegrees.length === 1 ? 'Your degree' : 'Your degrees'}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {myDegrees.map((degree) => {
              const dp = progressForDegree(degree.id)
              const done = dp.total > 0 && dp.completed === dp.total
              return (
                <div
                  key={degree.id}
                  className={`rounded-xl border p-6 ${
                    done ? 'border-green-300 bg-green-50/50' : 'border-indigo-200 bg-indigo-50/40'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold text-slate-800">{degree.name}</h3>
                    <span className="text-2xl font-bold tabular-nums text-slate-800">{dp.percent}%</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{degree.description}</p>
                  <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white">
                    <div
                      className={`h-full rounded-full ${done ? 'bg-green-500' : 'bg-indigo-500'}`}
                      style={{ width: `${dp.percent}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500 tabular-nums">
                    {dp.completed} of {dp.total} modules across {coursesIn(degree)} course
                    {coursesIn(degree) === 1 ? '' : 's'}
                    {dp.optionalCompleted > 0 && (
                      <span className="text-amber-600">
                        {' '}· plus {dp.optionalCompleted} optional
                      </span>
                    )}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Tracks */}
      <div className="max-w-4xl mx-auto px-6 py-14 w-full">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-5">
          {others.length > 0 ? 'Your courses' : 'Courses'}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {mine.map((track, idx) => {
            const tp = progressForCourse(track.id)
            const done = tp.total > 0 && tp.completed === tp.total

            return (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
              >
                <Link
                  to={courseHref(track.id)}
                  className={`flex flex-col h-full bg-white rounded-xl border p-6 transition-all hover:shadow-md ${
                    done ? 'border-green-200 bg-green-50/30' : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-800">{track.name}</h3>
                    {done && <CheckCircle2 size={16} className="text-green-500" />}
                  </div>
                  <p className="text-sm text-slate-500 flex-1">{track.description}</p>

                  <div className="mt-5">
                    <div className="flex justify-between text-xs text-slate-400 mb-1.5 tabular-nums">
                      <span>
                        {tp.completed} of {tp.total} module{tp.total === 1 ? '' : 's'}
                      </span>
                      <span>{tp.percent}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full ${done ? 'bg-green-500' : 'bg-blue-500'}`}
                        initial={{ width: 0 }}
                        animate={{ width: `${tp.percent}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                </Link>
              </motion.div>
            )
          })}
        </div>

        {others.length > 0 && (
          <>
            <h2 className="mt-12 mb-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
              Other courses
            </h2>
            <p className="mb-5 text-sm text-slate-500">
              Not assigned to you — but open if you want them, and your score counts the same.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {others.map((track) => (
                <Link
                  key={track.id}
                  to={courseHref(track.id)}
                  className="flex flex-col rounded-xl border border-dashed border-slate-300 bg-white/60 p-6 transition-all hover:border-blue-300 hover:shadow-sm"
                >
                  <h3 className="font-semibold text-slate-700">{track.name}</h3>
                  <p className="mt-1 flex-1 text-sm text-slate-500">{track.description}</p>
                  <span className="mt-4 text-xs font-medium text-blue-600">
                    {publishedInCourse(track, mergeCatalogue(builtInModules, customModules || [])).length}{' '}
                    modules · have a look
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Link to Build your own modules */}
        {(user?.isAdmin || user?.isManager) && (
          <div className="mt-16 text-center border-t border-slate-200 pt-8 pb-4">
            <Link
              to="/admin/docs"
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:text-indigo-600 hover:border-indigo-200 shadow-xs transition-all hover:shadow-sm"
            >
              <Sparkles className="h-4 w-4 text-indigo-500" />
              <span>Want to build your own training modules or create team degrees?</span>
              <span className="text-indigo-600 inline-flex items-center gap-0.5">Admin Guides & Studio <ArrowRight className="h-3 w-3" /></span>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
